import { BACKOFFICE_ORGANIZATIONS_MONITORING } from './backoffice-organizations-monitoring'

export const BACKOFFICE_ORGANIZATIONS = {
  MANAGE: {
    TITLE: 'Manage organization',
    TABS: {
      LINKED_DOCUMENTS: 'Linked Documents',
      EMPLOYEE_CENTER: 'Employee Center',
      MONITORING: 'Monitoring',
      TRUST_CENTER: 'Trust Center',
    },
    EMPLOYEE_CENTER: {
      DESCRIPTION: 'Choose which documents this organization shows its employees in the Employee Center.',
      VISIBLE_TOOLTIP:
        'Turning a document on makes it eligible. It reaches employees once it is translated into this ' +
        "organization's language and has an approved version, or a usable document link for " +
        'organizations still on Drive.',
      ALL_DEPARTMENTS: 'All departments',
      NO_APPROVED_VERSION: 'On, but not yet shown: no approved version',
      MISSING_TRANSLATION: "On, but not yet shown: no translation in this organization's language",
      ENABLED_COUNT: '{count} shown to employees',
      HIDDEN_BY_FILTER: '{count} enabled outside this filter',
      NEVER_REVIEWED: '{count} never reviewed',
      NEVER_REVIEWED_OTHER_TYPES: 'Documents in {count} other types have never been reviewed',
      SHOW_ALL_TYPES: 'Show all types',
      ENABLE_ALL_IN_VIEW: 'Enable all shown',
      ENABLING: 'Enabling...',
      ALL_TYPES: 'All types',
      SEARCH_PLACEHOLDER: 'Search by code or name...',
      LOADING: 'Loading...',
      REFRESH: 'Refresh',
      EMPTY: 'No documents match.',
      TABLE: {
        CODE: 'Code',
        NAME: 'Name',
        TYPE: 'Type',
        STATUS: 'Status',
        VISIBLE: 'Employee Center',
        DEPARTMENTS: 'Departments',
      },
      SNACKBAR: {
        SAVED: 'Changes saved',
      },
    },
    LINKED_DOCUMENTS: {
      PRIMARY_ACTION: 'Link Google Drive documents',
      LINKING: 'Linking...',
      DESCRIPTION: `Link shared Google Drive URLs to this organization’s compliance documents.`,
      MIGRATION_BLOCKED:
        'This organization has documents migrated in-platform. Linking Google Drive documents is disabled to avoid overwriting them. Registers are spreadsheets and cannot be migrated, so use “Clone registers from Drive” for those.',
      CLONE_REGISTERS: 'Clone registers from Drive',
      CLONING: 'Cloning...',
      CLONE_REGISTERS_HINT:
        'Copies the register spreadsheets from the Drive source folder and links them. Safe to re-run, and it never touches documents that have an in-platform version.',
      PROGRESS: {
        TITLE: 'Linking in progress',
        STARTING: 'Starting…',
        LINKING: 'Copying and mapping documents…',
        REFRESHING: 'Refreshing linked documents…',
      },
      CLONE_PROGRESS: {
        TITLE: 'Cloning registers',
        STARTING: 'Starting…',
        CLONING: 'Copying register spreadsheets…',
        REFRESHING: 'Refreshing linked documents…',
      },
      SEARCH_PLACEHOLDER: 'Search by code or name…',
      REFRESH: 'Refresh',
      LOADING: 'Loading...',
      EMPTY: 'No documents found.',
      URL_PLACEHOLDER: 'https://…',
      OPEN: 'Open',
      COPY: 'Copy',
      SAVE: 'Save',
      SAVING: 'Saving...',
      TABLE: {
        CODE: 'Code',
        NAME: 'Name',
        URL: 'Document URL',
        ACTIONS: 'Actions',
      },
      SNACKBAR: {
        LINKED: ({ mappedCount }: { mappedCount: number }) => `Linked ${mappedCount} document URLs`,
        LINKED_WITH_WARNINGS: ({
          mappedCount,
          missingCode,
          missingUrl,
        }: {
          mappedCount: number
          missingCode: number
          missingUrl: number
        }) =>
          `Linked ${mappedCount} document URLs (skipped: ${missingCode} missing codes, ${missingUrl} missing links)`,
        REGISTERS_CLONED: ({ clonedCount, mappedCount }: { clonedCount: number; mappedCount: number }) =>
          `Cloned ${clonedCount} register file(s), linked ${mappedCount} document(s)`,
        REGISTERS_CLONED_WITH_WARNINGS: ({
          clonedCount,
          mappedCount,
          missingCode,
          missingUrl,
        }: {
          clonedCount: number
          mappedCount: number
          missingCode: number
          missingUrl: number
        }) =>
          `Cloned ${clonedCount} register file(s), linked ${mappedCount} document(s) (skipped: ${missingCode} without a document code, ${missingUrl} without a link)`,
        REGISTERS_UP_TO_DATE: ({ mappedCount }: { mappedCount: number }) =>
          `No new register files to clone; ${mappedCount} document(s) already linked`,
        SAVED: 'URL saved',
        COPIED: 'Copied to clipboard',
        COPY_FAILED: 'Copy failed',
      },
    },
    MONITORING: BACKOFFICE_ORGANIZATIONS_MONITORING,
    TRUST_CENTER: {
      TAB_LABEL: 'Trust Center',
      BANNER: {
        TITLE: 'Publishing warning',
        DESCRIPTION: 'Saving any section makes the Trust Center publicly accessible.',
        URL_LABEL: 'Public URL',
      },
      ORG_DATA: {
        TITLE: 'Organization data',
        TAGLINE_LABEL: 'Tagline',
        TAGLINE_PLACEHOLDER: 'Your company mission or security statement',
        SHOW_CONTROLS_LABEL: 'Show Controls section',
        SHOW_CONTROLS_DESCRIPTION:
          'When enabled, the Controls tab and overview are visible to public trust center visitors.',
        VULNERABILITY_EMAIL_LABEL: 'Vulnerability reports email',
        QUESTIONNAIRES_EMAIL_LABEL: 'Security questionnaires email',
        CONTACT_EMAILS_DESCRIPTION:
          'Shown on the public Trust Center contact cards. Leave empty to use the Qalea inbox.',
        SNACKBAR_SAVED: 'Trust Center data saved',
        SNACKBAR_ERROR: 'Error saving Trust Center data',
      },
      STANDARDS: {
        TITLE: 'Standards',
        LABEL: 'Standards shown in Trust Center',
        PLACEHOLDER: 'Select standards...',
        SEARCH_PLACEHOLDER: 'Search standards...',
        EMPTY_TEXT: 'No standards found.',
        SNACKBAR_SAVED: 'Standards saved',
        SNACKBAR_ERROR: 'Error saving standards',
      },
      SUBPROCESSORS: {
        TITLE: 'Subprocessors',
        ADD: 'Add subprocessor',
        EMPTY: 'No subprocessors added yet.',
        NAME_LABEL: 'Name',
        LOGO_URL_LABEL: 'Logo URL',
        PURPOSE_LABEL: 'Purpose',
        LOCATION_LABEL: 'Location',
        SAVE: 'Save',
        CANCEL: 'Cancel',
        EDIT: 'Edit',
        DELETE: 'Delete',
        TABLE: {
          NAME: 'Name',
          PURPOSE: 'Purpose',
          LOCATION: 'Location',
          ACTIONS: 'Actions',
        },
        SNACKBAR_ADDED: 'Subprocessor added',
        SNACKBAR_UPDATED: 'Subprocessor updated',
        SNACKBAR_DELETED: 'Subprocessor deleted',
        SNACKBAR_ERROR: 'Error updating subprocessors',
      },
      RESOURCES: {
        TITLE: 'Resources',
        ADD: 'Add resource',
        EMPTY: 'No resources added yet.',
        NAME_LABEL: 'Name',
        DESCRIPTION_LABEL: 'Description',
        URL_LABEL: 'URL (optional)',
        TYPE_LABEL: 'Type',
        TYPE_PUBLIC: 'Public',
        TYPE_PRIVATE: 'Private',
        SAVE: 'Save',
        CANCEL: 'Cancel',
        EDIT: 'Edit',
        DELETE: 'Delete',
        TABLE: {
          NAME: 'Name',
          DESCRIPTION: 'Description',
          TYPE: 'Type',
          ACTIONS: 'Actions',
        },
        SNACKBAR_ADDED: 'Resource added',
        SNACKBAR_UPDATED: 'Resource updated',
        SNACKBAR_DELETED: 'Resource deleted',
        SNACKBAR_ERROR: 'Error updating resources',
      },
    },
    SAVE: {
      LABEL: 'Save changes',
      SAVING: 'Saving...',
    },
  },
}
